import { Component, ErrorInfo, ReactNode } from 'react'
import { locale } from '@/locales'
import { appConfig } from '@/config/app'
import { appLogger } from '@/utils/logger'

interface Props {
    children: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    }

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        appLogger.error('Uncaught error', error, errorInfo)
    }

    private handleReload = () => {
        window.location.reload()
    }

    private handleGoHome = () => {
        window.location.href = '/'
    }

    public render() {
        const uiColors = appConfig.ui.colors

        if (this.state.hasError) {
            return (
                <div className={`flex flex-col items-center justify-center min-h-screen ${uiColors.pageBg} p-4 text-center`}>
                    <h1 className={`text-2xl font-bold ${uiColors.textPrimary} mb-4`}>{locale.errorBoundary.title}</h1>
                    <p className={`${uiColors.textMuted} mb-8 max-w-xs`}>
                        {locale.errorBoundary.description}
                    </p>
                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={this.handleReload}
                            className={`${uiColors.accentBlueBg} ${uiColors.textPrimary} px-6 py-3 rounded-xl font-medium active:opacity-80 transition-opacity`}
                        >
                            {locale.errorBoundary.reload}
                        </button>
                        <button
                            type="button"
                            onClick={this.handleGoHome}
                            className={`${uiColors.surfaceMid} ${uiColors.textPrimary} px-6 py-3 rounded-xl font-medium active:opacity-80 transition-opacity`}
                        >
                            {locale.errorBoundary.goHome}
                        </button>
                    </div>
                    {import.meta.env.DEV && this.state.error && (
                        <pre className={`mt-8 p-4 ${uiColors.surfaceDarker} rounded ${uiColors.textDanger} text-xs text-left w-full overflow-auto max-h-40`}>
                            {this.state.error.toString()}
                        </pre>
                    )}
                </div>
            )
        }

        return this.props.children
    }
}
